import React, { useState } from 'react';
import './RankExplanation.css';

/**
 * Score Bar Component
 * Visual bar showing score percentage
 */
function ScoreBar({ value, maxValue = 100, color = 'primary' }) {
  const percentage = Math.min((value / maxValue) * 100, 100);
  return (
    <div className="score-bar">
      <div 
        className={`score-bar-fill score-bar-${color}`} 
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
}

/**
 * Platform Contribution Bar
 * Shows platform's contribution to overall engagement
 */
function PlatformBar({ platform, data, isTop }) {
  const percentage = (data.contribution || 0) * 100;
  const platformIcons = {
    tiktok: '🎵',
    youtube: '📺',
    instagram: '📸',
    twitter: '🐦',
    reddit: '🔴',
    unknown: '📱',
  };
  
  return (
    <div className={`platform-bar-item ${isTop ? 'top-platform' : ''}`}>
      <div className="platform-bar-header">
        <span className="platform-icon">{platformIcons[platform.toLowerCase()] || '📱'}</span>
        <span className="platform-name">{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
        <span className="platform-count">{data.count} videos</span>
        <span className="platform-pct">{percentage.toFixed(0)}%</span>
      </div>
      <div className="platform-bar">
        <div 
          className="platform-bar-fill" 
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="platform-metrics">
        {data.total_likes > 0 && (
          <span className="metric">❤️ {formatNumber(data.total_likes)}</span>
        )}
        {data.total_comments > 0 && (
          <span className="metric">💬 {formatNumber(data.total_comments)}</span>
        )}
        {data.total_shares > 0 && (
          <span className="metric">🔄 {formatNumber(data.total_shares)}</span>
        )}
      </div>
    </div>
  );
}

/**
 * Format large numbers
 */
function formatNumber(num) {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toString() || '0';
}

/**
 * Compact Rank Explanation
 * Shows quick summary for TagCard
 */
export function RankExplanationCompact({ rankExplanation, trendingScore }) {
  if (!rankExplanation) return null;
  
  const { primary_factor } = rankExplanation;
  
  const factorIcons = {
    media_quality: '⭐',
    frequency: '📊',
    cross_platform: '🌐',
    recency: '🕐',
  };
  
  return (
    <div className="rank-explanation-compact">
      <div className="rank-primary-factor">
        <span className="factor-icon">
          {factorIcons[primary_factor?.factor] || '📈'}
        </span>
        <span className="factor-text">{primary_factor?.explanation}</span>
      </div>
    </div>
  );
}

/**
 * Full Rank Explanation Component
 * Detailed breakdown for TagDetailView
 */
function RankExplanation({ 
  rankExplanation, 
  trendingScore, 
  trendingRank,
  expanded: initialExpanded = false 
}) {
  const [expanded, setExpanded] = useState(initialExpanded);
  
  if (!rankExplanation) {
    return (
      <div className="rank-explanation empty">
        <p className="no-data">Rank explanation not available</p>
      </div>
    );
  }

  const { 
    primary_factor, 
    score_weights, 
    platform_breakdown, 
    summary,
    total_score 
  } = rankExplanation;

  const factorIcons = {
    media_quality: '⭐',
    frequency: '📊',
    cross_platform: '🌐',
    recency: '🕐',
  };

  const platforms = Object.entries(platform_breakdown || {});

  return (
    <div className="rank-explanation">
      {/* Header with rank and score */}
      <div className="rank-header">
        {trendingRank && (
          <div className="rank-badge">
            <span className="rank-number">#{trendingRank}</span>
            <span className="rank-label">Rank</span>
          </div>
        )}
        <div className="score-badge">
          <span className="score-number">{(trendingScore || total_score || 0).toFixed(1)}</span>
          <span className="score-label">Score</span>
        </div>
      </div>

      {/* Primary Factor */}
      <div className="rank-primary">
        <div className="primary-icon">
          {factorIcons[primary_factor?.factor] || '📈'}
        </div>
        <div className="primary-content">
          <span className="primary-label">Primary Driver</span>
          <span className="primary-text">{primary_factor?.explanation}</span>
        </div>
        <div className="primary-contribution">
          +{(primary_factor?.contribution || 0).toFixed(1)} pts
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="rank-summary">
          <p>{summary}</p>
        </div>
      )}

      {/* Expandable Details */}
      <button 
        className="expand-btn"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? 'Hide Details' : 'Show Score Breakdown'}
        <span className={`expand-icon ${expanded ? 'expanded' : ''}`}>▼</span>
      </button>

      {expanded && (
        <div className="rank-details">
          {/* Score Breakdown */}
          <div className="score-breakdown">
            <h4>Score Components</h4>
            {Object.entries(score_weights || {}).map(([key, data]) => (
              <div key={key} className="score-component">
                <div className="component-header">
                  <span className="component-icon">{factorIcons[key] || '📊'}</span>
                  <span className="component-label">{data.label}</span>
                  <span className="component-values">
                    <span className="raw-score">{data.raw_score}</span>
                    <span className="weight">×{data.weight}</span>
                    <span className="weighted-score">= {data.weighted}</span>
                  </span>
                </div>
                <ScoreBar value={data.raw_score} />
                <p className="component-desc">{data.description}</p>
              </div>
            ))}
          </div>

          {/* Platform Breakdown */}
          {platforms.length > 0 && (
            <div className="platform-breakdown">
              <h4>Platform Contribution</h4>
              {platforms.map(([platform, data], idx) => (
                <PlatformBar 
                  key={platform} 
                  platform={platform} 
                  data={data}
                  isTop={idx === 0}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RankExplanation;
